import { Injectable, Injector } from '@angular/core';
import { GetSponsorshipDto, SponsorshipServiceProxy, CreateOrUpdateSponsorshipDto, SponsorshipInvoiceAddressDto, BusinessServiceProxy, SponsorshipDto, CorporateLicenceType, BankDetailsServiceProxy, UpdateBankDetailsInput, GetBankDetailsDto } from '@shared/service-proxies/service-proxies';
import { SelectItem } from 'primeng/api';
import { tap, map, of, ReplaySubject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { AppConsts } from '../../../shared/AppConsts';
import { AppComponentBase } from '../../../shared/common/app-component-base';
import { CodeService } from '../common/code/code.service';
import { ArchiveService } from './archive.service';
import { LocalizationService } from '@node_modules/abp-ng2-module';

export interface ISponsorshipDataCache {
    name: string | undefined;
    uid: string | undefined;
}

@Injectable({
    providedIn: 'root',
})
export class SponsorshipDossierService extends AppComponentBase {

    sponsorship: GetSponsorshipDto = null;
    private sponsorshipDataSubject = new ReplaySubject<GetSponsorshipDto>(1);
    readonly sponsorshipData$ = this.sponsorshipDataSubject.asObservable();

    constructor(
        injector: Injector,
        private codeService: CodeService,
        private sponsorshipService: SponsorshipServiceProxy,
        private readonly bankDetailsService: BankDetailsServiceProxy,
        private businessService: BusinessServiceProxy
    ){
        super(injector);
    }

    clear() {
        this.sponsorship = undefined;
        this.sponsorshipDataSubject.next(null);
        this.archiveService.resetArchivedFlag();
    }

    public GetSponsorshipDossiers(filter: string, sorting: string, maxResultCount: number, skipCount: number, isArchived = false) {
        return this.sponsorshipService.getDossiers(isArchived, filter, sorting, maxResultCount, skipCount);
    }

    public LoadSponsorship(id: number): Observable<GetSponsorshipDto> {
        return this.sponsorshipService.getSponsorship(id).pipe(
            tap((sponsorship: GetSponsorshipDto) => {
                this.sponsorship = sponsorship;
                this.sponsorshipDataSubject.next(sponsorship);
                this.archiveService.setArchivedFlag(sponsorship.isArchived)
            })
        );
    }

    public UpdateSponsorshipCoreData(input: CreateOrUpdateSponsorshipDto): Observable<GetSponsorshipDto> {
        return this.sponsorshipService.update(input).pipe(tap((sponsorship: GetSponsorshipDto) => {
                this.sponsorship = sponsorship;
                this.sponsorshipDataSubject.next(sponsorship);
            }));
    }

    SetArchiveFlag(isArchived: boolean): Observable<void> {
        return this.sponsorshipService.setArchiveFlag(this.sponsorship.id, isArchived).pipe(tap(x => {
            this.archiveService.setArchivedFlag(isArchived);
        }));
    }

    public get sponsorshipDossierBasePath(): string {
        return this.sponsorship ? `/app/authority/sponsorship-dossier/${this.sponsorship.id}/` : null;
    }

    public GetId(): number {
        return this.sponsorship?.id;
    }

    public GetUid(): string {
        return this.sponsorship.uid;
    }

    public GetSponsorship(): Observable<GetSponsorshipDto> {
        return of(this.sponsorship);
    }

    createEmploymentAddressDropdownList(includeSponsorship: boolean = false): Observable<SelectItem[]> {

        return this.businessService.getBusinessesForSponsorship(this.sponsorship.id).pipe(map(businesses => {
            let dropdownList: SelectItem[] = [];

            if (!this.sponsorship.isPublicPharmacy) {
                dropdownList.push({ value: AppConsts.employmentAddressType.mobile, label: this.l('MobileAddress') });
            }

            if (includeSponsorship) {
                dropdownList.push({ value: AppConsts.employmentAddressType.sponsorship, label: this.l('SponsorshipAddress') });
            }

            businesses.forEach(business => {
                dropdownList.push({ value: business.id, label: this.l('Business') + ': ' + this.l(business.name) });
            });

            return dropdownList;
        }));
    }

    public GetInvoiceAddress() {
        return this.sponsorshipService.getInvoiceAddress(this.sponsorship.id);
    }

    public UpdateInvoiceAddress(input: SponsorshipInvoiceAddressDto) {
        return this.sponsorshipService.updateInvoiceAddress(this.sponsorship.id, input);
    }

    getCorporateLicenceTypes(allOnTop: boolean) {
        return this.codeService.getEnumForDropdown('CorporateLicenceType', CorporateLicenceType, allOnTop);
    }

    public getBankDetails(): Observable<GetBankDetailsDto> {
        return this.bankDetailsService.getSponsorshipBankDetails(this.sponsorship.id);
    }

    public updateBankDetails(input: UpdateBankDetailsInput): Observable<number> {
        input.entityId = this.sponsorship.id;
        return this.bankDetailsService.saveSponsorshipBankDetails(input);
    }
}
